import { Card } from '../../ui'
import styles from './TrendChart.module.css'

interface ChartSeries {
  key: string
  label: string
  series: number[]
  prev: number[]
}

interface ChartMarker {
  n: number
  dayIndex: number
  axis: string
}

interface ChartData {
  title: string
  left: ChartSeries
  right: ChartSeries
  markers: ChartMarker[]
}

interface TrendChartProps {
  chart: Record<string, unknown>
  days: string[]
  compare: boolean
  highlightedMarker: number | null
  onMarkerHover: (marker: number | null) => void
}

const width = 706
const height = 336
const divisions = 5
const margin = { left: 62, right: 56, top: 46, bottom: 38 }
const plotWidth = width - margin.left - margin.right
const plotHeight = height - margin.top - margin.bottom

function axisMax(value: number): number {
  if (value <= 0) return divisions
  const need = value / divisions
  const exponent = 10 ** Math.floor(Math.log10(need))
  for (const multiple of [1, 2, 2.5, 4, 5, 10]) {
    if (need <= multiple * exponent) return multiple * exponent * divisions
  }
  return 10 * exponent * divisions
}

function compact(value: number): string {
  if (value >= 1_000_000) return `${value / 1_000_000}M`
  if (value >= 1_000) return `${value / 1_000}K`
  return String(value)
}

function formatAxis(value: number, currency: boolean): string {
  return `${currency ? '$' : ''}${compact(value)}`
}

export function TrendChart({ chart: rawChart, days, compare, highlightedMarker, onMarkerHover }: TrendChartProps) {
  const chart = rawChart as unknown as ChartData
  const leftValues = compare ? [...chart.left.series, ...chart.left.prev] : chart.left.series
  const rightValues = compare ? [...chart.right.series, ...chart.right.prev] : chart.right.series
  const leftMax = axisMax(Math.max(...leftValues))
  const rightMax = axisMax(Math.max(...rightValues) * 1.25)
  const x = (index: number) => margin.left + (days.length > 1 ? plotWidth * index / (days.length - 1) : plotWidth / 2)
  const yLeft = (value: number) => margin.top + plotHeight - plotHeight * value / leftMax
  const yRight = (value: number) => margin.top + plotHeight - plotHeight * value / rightMax
  const path = (values: number[], y: (value: number) => number) => `M${values.map((value, index) => `${x(index).toFixed(1)} ${y(value).toFixed(1)}`).join(' L')}`
  const labelStep = Math.max(1, Math.floor(days.length / 7))
  const leftCurrency = chart.left.key === 'revenue' || chart.left.key === 'spend'

  return (
    <Card className={styles.card} data-scope="analytics-chart">
      <h3 className={styles.title}>{chart.title}</h3>
      <div data-context="chart-canvas">
        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={chart.title}
          style={{ maxWidth: '100%', height: 'auto', fontFamily: 'inherit' }}
        >
          {Array.from({ length: divisions + 1 }, (_, index) => {
            const leftValue = leftMax * index / divisions
            const rightValue = rightMax * index / divisions
            const y = yLeft(leftValue)
            return (
              <g key={`axis-${index}`}>
                <line x1={margin.left} y1={y.toFixed(1)} x2={margin.left + plotWidth} y2={y.toFixed(1)} stroke="#EDEFF3" strokeWidth="1" />
                <text x={margin.left - 12} y={(y + 4).toFixed(1)} textAnchor="end" fontSize="12" fill="#8A8D91">
                  {formatAxis(leftValue, leftCurrency)}
                </text>
                <text x={margin.left + plotWidth + 12} y={(y + 4).toFixed(1)} fontSize="12" fill="#F5720D">
                  {formatAxis(rightValue, true)}
                </text>
              </g>
            )
          })}
          {days.map((day, index) => (days.length <= 8 || index % labelStep === 0) ? (
            <text
              key={`${day}-${index}`}
              x={x(index).toFixed(1)}
              y={margin.top + plotHeight + 24}
              textAnchor="middle"
              fontSize="12"
              fill="#8A8D91"
            >
              {day}
            </text>
          ) : null)}
          <text x={margin.left - 46} y={margin.top - 12} fontSize="12" fill="#8A8D91">{chart.left.label}</text>
          <text x={margin.left + plotWidth + 12} y={margin.top - 12} fontSize="12" fill="#F5720D">{chart.right.label}</text>
          <g transform={`translate(${margin.left + plotWidth / 2 - 88},18)`}>
            <line x1="0" y1="0" x2="22" y2="0" stroke="#0866FF" strokeWidth="2.4" />
            <text x="30" y="4" fontSize="12.5" fill="#050505">{chart.left.label}</text>
            <line x1="96" y1="0" x2="118" y2="0" stroke="#F5720D" strokeWidth="2.4" />
            <text x="126" y="4" fontSize="12.5" fill="#050505">{chart.right.label}</text>
          </g>
          {compare ? (
            <>
              <path d={path(chart.left.prev, yLeft)} fill="none" stroke="#0866FF" strokeWidth="1.6" strokeDasharray="5 4" opacity=".34" />
              <path d={path(chart.right.prev, yRight)} fill="none" stroke="#F5720D" strokeWidth="1.6" strokeDasharray="5 4" opacity=".34" />
            </>
          ) : null}
          <path d={path(chart.right.series, yRight)} fill="none" stroke="#F5720D" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
          <path d={path(chart.left.series, yLeft)} fill="none" stroke="#0866FF" strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
          {chart.left.series.map((value, index) => (
            <circle
              key={`point-${index}`}
              cx={x(index).toFixed(1)}
              cy={yLeft(value).toFixed(1)}
              r="3.4"
              fill="#fff"
              stroke="#0866FF"
              strokeWidth="2"
            />
          ))}
          {chart.markers.map((marker) => {
            const cx = x(marker.dayIndex)
            const cy = yLeft(chart.left.series[marker.dayIndex] ?? 0)
            const highlighted = highlightedMarker === marker.n
            return (
              <g
                className={styles.marker}
                key={marker.n}
                aria-label={`摘要 ${marker.n}`}
                onMouseEnter={() => onMarkerHover(marker.n)}
                onMouseLeave={() => onMarkerHover(null)}
              >
                <circle className={`${styles.markerRing} ${highlighted ? styles.markerRingVisible : ''}`} cx={cx.toFixed(1)} cy={cy.toFixed(1)} r="18" />
                <circle className={styles.markerCore} cx={cx.toFixed(1)} cy={cy.toFixed(1)} r={highlighted ? 13 : 11} fill="#0866FF" />
                <text x={cx.toFixed(1)} y={(cy + 4).toFixed(1)} textAnchor="middle" fontSize="12" fontWeight="600" fill="#fff">{marker.n}</text>
              </g>
            )
          })}
        </svg>
      </div>
    </Card>
  )
}
